import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

/**
 * Composable central de permissões do módulo Pipeline/CRM.
 *
 * As permission_keys chegam do backend via AccountUser#permissions
 * (sobrescrito pelo PipelinePermissionsExtensions concern) e são
 * serialzadas em _user.json.jbuilder → account_user.permissions.
 *
 * Uso:
 *   const { can, canAny, isAdmin, isPro } = usePipelinePermissions()
 *   v-if="can('pipeline.view')"
 *   v-if="canAny(['deals.create', 'deals.edit'])"
 */
export function usePipelinePermissions() {
  const currentUser = useMapGetter('getCurrentUser');
  const currentAccountId = useMapGetter('getCurrentAccountId');

  // Array de permissions do usuário logado na account atual
  const userPermissions = computed(() => {
    const account = (currentUser.value?.accounts || []).find(
      a => Number(a.id) === Number(currentAccountId.value)
    );
    return account?.permissions || [];
  });

  const isAdmin = computed(() =>
    userPermissions.value.includes('administrator')
  );

  // Slug do plano da account — vem do store pipelineRoles após fetch
  const planSlug = useMapGetter('pipelineRoles/getPermissionKeys');
  const isPro = computed(() => planSlug.value?.planSlug === 'pro');
  const isEssencial = computed(() => planSlug.value?.planSlug === 'essencial');
  const isStarter = computed(() => planSlug.value?.planSlug === 'starter');

  /**
   * Verifica se o usuário tem uma permission key específica.
   * Administradores sempre retornam true.
   */
  const can = key => {
    if (isAdmin.value) return true;
    return userPermissions.value.includes(key);
  };

  /**
   * Verifica se o usuário tem pelo menos uma das keys.
   */
  const canAny = (keys = []) => {
    if (isAdmin.value) return true;
    return keys.some(k => userPermissions.value.includes(k));
  };

  /**
   * Verifica se o usuário tem todas as keys.
   */
  const canAll = (keys = []) => {
    if (isAdmin.value) return true;
    return keys.every(k => userPermissions.value.includes(k));
  };

  /**
   * Na tela de permissões: quais keys são editáveis para o plano atual.
   * Pro → todas. Starter/Essencial → só visibilidade de conversas.
   */
  const editablePermissionKeys = computed(() => {
    const keys = planSlug.value?.editableForPlan || [];
    return keys;
  });

  /**
   * Planos Starter e Essencial não podem criar roles customizados.
   */
  const canCreateCustomRoles = computed(() => isPro.value);

  return {
    can,
    canAny,
    canAll,
    isAdmin,
    isPro,
    isEssencial,
    isStarter,
    userPermissions,
    editablePermissionKeys,
    canCreateCustomRoles,
  };
}
